import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Copy, RotateCw, Ban } from "lucide-react";
import InviteDialog, { copyLink } from "@/components/InviteDialog";
import { useNavigate } from "react-router-dom";

const STATUS = {
  pending: ["En attente", "bg-[#E0C173]/20 text-[#8a6d22] border-[#E0C173]"],
  accepted: ["Compte créé", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  expired: ["Expirée", "bg-gray-100 text-gray-600 border-gray-300"],
  revoked: ["Annulée", "bg-red-50 text-red-700 border-red-200"],
};

const Row = ({ i, reload }) => {
  const [label, cls] = STATUS[i.status] || STATUS.pending;
  const act = async (fn, msg) => { try { const { data } = await fn(); toast.success(data?.email_sent === false ? "Lien renouvelé (email non envoyé, copiez le lien)" : msg); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div data-testid={`invitation-${i.invitation_id}`} className="bg-white border border-gray-200 p-4 flex flex-wrap items-center gap-4 justify-between">
      <div>
        <div className="font-medium text-[#1B2A4A]">{i.first_name} {i.last_name} <span className="text-sm font-normal text-[#475569]">· {i.email}</span></div>
        <div className="text-xs text-gray-500 mt-0.5">Envoyée le {new Date(i.created_at).toLocaleDateString("fr-FR")} par {i.invited_by}{i.status === "pending" && ` · expire le ${new Date(i.expires_at).toLocaleDateString("fr-FR")}`}{i.kind === "onsite" && " · créé pendant le RDV"}{i.kind === "onsite" && !i.access_sent_at && " (accès non envoyé)"}{i.reminder_sent_at && ` · relancée le ${new Date(i.reminder_sent_at).toLocaleDateString("fr-FR")}`}{i.accepted_at && ` · compte créé le ${new Date(i.accepted_at).toLocaleDateString("fr-FR")}`}</div>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs px-3 py-1 border ${cls}`} data-testid={`invitation-status-${i.invitation_id}`}>{label}</span>
        {(i.status === "pending" || i.status === "expired") && (
          <>
            {i.link && <button title="Copier le lien" data-testid={`invitation-copy-${i.invitation_id}`} onClick={() => copyLink(i.link)} className="p-2 border border-gray-300 hover:border-[#1B2A4A]"><Copy className="w-4 h-4" /></button>}
            <button title="Renvoyer" data-testid={`invitation-resend-${i.invitation_id}`} onClick={() => act(() => api.post(`/admin/invitations/${i.invitation_id}/resend`), "Invitation renvoyée")} className="p-2 border border-gray-300 hover:border-[#1B2A4A]"><RotateCw className="w-4 h-4" /></button>
            <button title="Annuler" data-testid={`invitation-revoke-${i.invitation_id}`} onClick={() => window.confirm("Annuler cette invitation ?") && act(() => api.delete(`/admin/invitations/${i.invitation_id}`), "Invitation annulée")} className="p-2 border border-gray-300 hover:border-red-400 hover:text-red-600"><Ban className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
  );
};

export default function InvitationsPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => api.get("/admin/invitations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="invitations-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#475569] max-w-xl">Après un RDV découverte, invitez le dirigeant à créer son espace cédant et à compléter la fiche privée de son entreprise.</p>
        <div className="flex flex-wrap gap-2">
        <button data-testid="invitation-onsite" onClick={() => navigate("/admin/rdv")} className="inline-flex items-center gap-2 bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-5 py-2.5 text-sm"><UserPlus className="w-4 h-4" /> Démarrer pendant le RDV</button>
        <button data-testid="invitation-new" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white px-5 py-2.5 text-sm"><UserPlus className="w-4 h-4" /> Nouvelle invitation</button>
        </div>
      </div>
      {items.length === 0 && <p data-testid="invitations-empty" className="text-gray-500 py-10 text-center">Aucune invitation envoyée.</p>}
      {items.map((i) => <Row key={i.invitation_id} i={i} reload={load} />)}
      <InviteDialog open={open} onOpenChange={setOpen} onSent={load} />
    </div>
  );
}
